/**
 * Agent state store — non-chat side-channel state pushed from skills.
 *
 * The Gateway emits `state_snapshot` SSE events whose payload carries a
 * `namespace` (e.g. "ivt.motion.wave") and an arbitrary `snapshot` object.
 * Frontend panels subscribe to a namespace and re-render when its snapshot
 * changes — distinct from chat-stream rendering.
 *
 * Implementation note: this is a tiny external-store, used via React 18's
 * `useSyncExternalStore`. We deliberately avoid a Context Provider because
 * the store is global and read by panels far from the chat root; threading
 * a Provider would not add value here.
 */

import { useSyncExternalStore } from 'react';

type Snapshot = unknown;
type Listener = () => void;

const snapshots = new Map<string, Snapshot>();
const listeners = new Set<Listener>();

function emit(): void {
  // Copy to allow listeners that unsubscribe themselves during the call.
  for (const l of [...listeners]) l();
}

/** Write a fresh snapshot for the given namespace (called by ChatPanel SSE handler). */
export function setAgentState(namespace: string, snapshot: Snapshot): void {
  snapshots.set(namespace, snapshot);
  emit();
}

/** Clear the snapshot for a namespace (e.g. when a panel unmounts). */
export function clearAgentState(namespace: string): void {
  if (snapshots.delete(namespace)) emit();
}

/** Imperative read (rare — prefer the hook). */
export function getAgentState(namespace: string): Snapshot | undefined {
  return snapshots.get(namespace);
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Subscribe a React component to one namespace. The component re-renders
 * whenever `setAgentState(namespace, ...)` is called.
 *
 * Generic `T` is the snapshot shape the caller expects; it is the caller's
 * responsibility to validate the runtime shape (the store treats payloads
 * as `unknown`).
 */
export function useAgentState<T = unknown>(namespace: string): T | undefined {
  return useSyncExternalStore(
    subscribe,
    () => snapshots.get(namespace) as T | undefined,
    () => undefined, // server snapshot — Portal is client-only
  );
}
